import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMinisiteSessionAnalytics } from '../analytics/webAnalytics';
import MonthlyLeadersScreen from './minisite/MonthlyLeadersScreen';
import RemoteNameKeyboard from './minisite/RemoteNameKeyboard';
import { fetchMinisiteLeaderboard, submitMinisiteLeaderboard } from '../api/minisiteLeaderboard';
import { qualifiesForLeaderboard } from '../utils/minisiteLeaderboardQualify';
import './SpaceInvadersGame.css';
import './minisite/MinisiteShared.css';

const COLS = 28;
const ROWS = 24;
const CHROME_H = 118;
const CHROME_W = 16;

const ALIEN_COLS = 8;
const ALIEN_ROWS = 4;
const ALIEN_CELL = 2;
const STEP = ALIEN_CELL + 1;
const PLAYER_ROW = ROWS - 2;
const SHIP_W = 3;
const SHIELD_CHARGES_MAX = 3;
const LIVES_START = 3;
const LIVES_MAX = 99;
const BONUS_SHOTS_MIN = 8;
const BONUS_SHOTS_MAX = 28;
const BONUS_SHIP_W = 4.6;
const BONUS_SHIP_H = 2.15;
const BONUS_SHIP_VY = -0.42;
const EXPLOSION_FRAMES = 22;
const SCORE_CAP = 500000;

const COLORS = {
  bg: '#0a0f18',
  grid: '#1e293b',
  alien: '#4ade80',
  alienGlow: '#22c55e',
  player: '#5eead4',
  playerHullDark: '#0d9488',
  playerTrack: '#115e59',
  playerHighlight: '#ccfbf1',
  bulletPlayer: '#fef08a',
  bulletEnemy: '#f87171',
  shieldHint: '#fbbf24',
  bonusShip: '#e879f9',
  bonusGlow: '#c026d3',
  explosionOrange: '#f97316',
  explosionYellow: '#fbbf24',
};

const ALIEN_SHIP_VARIANTS = 4;

/** Pixel-art style alien “ships”; `variant` cycles each wave (0…3). */
function drawAlienShip(ctx, px, py, rW, rH, variant) {
  const pad = Math.max(1, Math.min(rW, rH) * 0.06);
  const x = px + pad;
  const y = py + pad;
  const w = Math.max(2, rW - 2 * pad);
  const h = Math.max(2, rH - 2 * pad);
  const cx = x + w / 2;
  const cy = y + h / 2;
  const grad = ctx.createLinearGradient(x, y, x + w, y + h);
  grad.addColorStop(0, COLORS.alienGlow);
  grad.addColorStop(1, COLORS.alien);
  ctx.fillStyle = grad;
  ctx.strokeStyle = '#166534';
  ctx.lineWidth = Math.max(1, Math.min(w, h) * 0.06);

  const v = variant % ALIEN_SHIP_VARIANTS;

  if (v === 0) {
    ctx.beginPath();
    ctx.ellipse(cx, cy + h * 0.12, w * 0.48, h * 0.28, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy - h * 0.02, w * 0.22, Math.PI, 0);
    ctx.lineTo(cx + w * 0.08, cy + h * 0.08);
    ctx.lineTo(cx - w * 0.08, cy + h * 0.08);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = 'rgba(20, 83, 45, 0.45)';
    ctx.beginPath();
    ctx.arc(cx - w * 0.18, cy + h * 0.18, w * 0.06, 0, Math.PI * 2);
    ctx.arc(cx + w * 0.18, cy + h * 0.18, w * 0.06, 0, Math.PI * 2);
    ctx.fill();
  } else if (v === 1) {
    ctx.beginPath();
    ctx.moveTo(cx, y + h * 0.08);
    ctx.lineTo(cx + w * 0.12, cy);
    ctx.lineTo(cx + w * 0.42, cy + h * 0.08);
    ctx.lineTo(cx + w * 0.42, cy + h * 0.42);
    ctx.lineTo(cx + w * 0.18, y + h * 0.92);
    ctx.lineTo(cx - w * 0.18, y + h * 0.92);
    ctx.lineTo(cx - w * 0.42, cy + h * 0.42);
    ctx.lineTo(cx - w * 0.42, cy + h * 0.08);
    ctx.lineTo(cx - w * 0.12, cy);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = COLORS.alienGlow;
    ctx.fillRect(cx - w * 0.08, cy + h * 0.12, w * 0.16, h * 0.35);
  } else if (v === 2) {
    const bx = x + w * 0.2;
    const by = y + h * 0.22;
    const bw = w * 0.6;
    const bh = h * 0.52;
    ctx.beginPath();
    ctx.rect(bx, by, bw, bh);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x + w * 0.12, cy + h * 0.1, h * 0.18, 0, Math.PI * 2);
    ctx.arc(x + w * 0.88, cy + h * 0.1, h * 0.18, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = COLORS.alienGlow;
    ctx.fillRect(cx - w * 0.06, y + h * 0.08, w * 0.12, h * 0.2);
  } else {
    ctx.beginPath();
    ctx.moveTo(cx, y + h * 0.1);
    ctx.lineTo(x + w * 0.88, cy + h * 0.35);
    ctx.lineTo(x + w * 0.55, y + h * 0.92);
    ctx.lineTo(x + w * 0.45, y + h * 0.92);
    ctx.lineTo(x + w * 0.12, cy + h * 0.35);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = 'rgba(20, 83, 45, 0.5)';
    ctx.beginPath();
    ctx.moveTo(cx, y + h * 0.22);
    ctx.lineTo(cx + w * 0.22, cy + h * 0.22);
    ctx.lineTo(cx, cy + h * 0.08);
    ctx.lineTo(cx - w * 0.22, cy + h * 0.22);
    ctx.closePath();
    ctx.fill();
  }
}

/** Tracked mobile cannon (replaces triangle ship). */
function drawMobileCannon(ctx, px, py, pw, ph, cell) {
  const main = COLORS.player;
  const dark = COLORS.playerHullDark;
  const track = COLORS.playerTrack;
  const hi = COLORS.playerHighlight;
  const cx = px + pw / 2;
  const wheelY = py + ph * 0.82;
  const wheelR = Math.max(2.5, cell * 0.14);

  ctx.fillStyle = track;
  ctx.beginPath();
  ctx.arc(px + pw * 0.22, wheelY, wheelR, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(px + pw * 0.78, wheelY, wheelR, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#042f2e';
  ctx.beginPath();
  ctx.arc(px + pw * 0.22, wheelY, wheelR * 0.42, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(px + pw * 0.78, wheelY, wheelR * 0.42, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = dark;
  ctx.fillRect(px + pw * 0.06, wheelY - wheelR * 0.32, pw * 0.88, wheelR * 0.64);

  ctx.fillStyle = main;
  ctx.beginPath();
  ctx.moveTo(px + pw * 0.18, py + ph * 0.72);
  ctx.lineTo(px + pw * 0.82, py + ph * 0.72);
  ctx.lineTo(px + pw * 0.68, py + ph * 0.36);
  ctx.lineTo(px + pw * 0.32, py + ph * 0.36);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = hi;
  ctx.lineWidth = Math.max(1, cell * 0.06);
  ctx.stroke();

  ctx.strokeStyle = hi;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(px + pw * 0.32, py + ph * 0.48);
  ctx.lineTo(px + pw * 0.68, py + ph * 0.48);
  ctx.stroke();

  const turR = Math.max(2, cell * 0.15);
  ctx.fillStyle = main;
  ctx.beginPath();
  ctx.arc(cx, py + ph * 0.34, turR, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = dark;
  ctx.lineWidth = 1;
  ctx.stroke();

  const barrelW = Math.max(2.5, cell * 0.22);
  const barrelTop = py + ph * 0.05;
  const barrelBot = py + ph * 0.28;
  ctx.fillStyle = dark;
  ctx.fillRect(cx - barrelW / 2, barrelTop, barrelW, barrelBot - barrelTop);
  ctx.fillStyle = hi;
  ctx.fillRect(cx - barrelW / 5, barrelTop + 1, (2 * barrelW) / 5, (barrelBot - barrelTop) * 0.45);

  ctx.fillStyle = track;
  ctx.fillRect(cx - barrelW * 0.75, barrelTop - Math.max(2, cell * 0.06), barrelW * 1.5, Math.max(2, cell * 0.08));
}

/** Large bonus cruiser (moves upward); distinct from formation aliens. */
function drawLargeBonusShip(ctx, px, py, pw, ph) {
  const cx = px + pw / 2;
  const grad = ctx.createLinearGradient(px, py, px + pw, py + ph);
  grad.addColorStop(0, COLORS.bonusGlow);
  grad.addColorStop(0.45, COLORS.bonusShip);
  grad.addColorStop(1, '#86198f');
  ctx.fillStyle = grad;
  ctx.strokeStyle = '#701a75';
  ctx.lineWidth = Math.max(1, Math.min(pw, ph) * 0.05);
  ctx.beginPath();
  ctx.ellipse(cx, py + ph * 0.62, pw * 0.48, ph * 0.32, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx, py + ph * 0.08);
  ctx.lineTo(px + pw * 0.88, py + ph * 0.42);
  ctx.lineTo(px + pw * 0.72, py + ph * 0.88);
  ctx.lineTo(px + pw * 0.28, py + ph * 0.88);
  ctx.lineTo(px + pw * 0.12, py + ph * 0.42);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = 'rgba(250, 250, 250, 0.35)';
  ctx.beginPath();
  ctx.arc(cx - pw * 0.2, py + ph * 0.52, ph * 0.12, 0, Math.PI * 2);
  ctx.arc(cx + pw * 0.2, py + ph * 0.52, ph * 0.12, 0, Math.PI * 2);
  ctx.fill();
}

function drawBonusExplosion(ctx, cell, gx, gy, progress) {
  const cx = gx * cell;
  const cy = gy * cell;
  const r = cell * (0.8 + progress * 5);
  const alpha = Math.max(0, 1 - progress);
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = COLORS.explosionOrange;
  ctx.lineWidth = Math.max(2, cell * 0.12);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = COLORS.explosionYellow;
  ctx.lineWidth = Math.max(1, cell * 0.08);
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.5, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = '#fff7ed';
  ctx.lineWidth = 1;
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2 + progress * 2.5;
    const len = r * (1.05 + 0.15 * Math.sin(progress * Math.PI * 2 + i));
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len);
    ctx.stroke();
  }
  ctx.restore();
}

function randomBonusShotThreshold() {
  return BONUS_SHOTS_MIN + Math.floor(Math.random() * (BONUS_SHOTS_MAX - BONUS_SHOTS_MIN + 1));
}

function spawnBonusShip() {
  const margin = 1;
  const maxX = COLS - BONUS_SHIP_W - margin;
  const x = margin + Math.random() * Math.max(0.1, maxX - margin);
  return {
    x,
    y: ROWS - 3.2,
    w: BONUS_SHIP_W,
    h: BONUS_SHIP_H,
    vy: BONUS_SHIP_VY,
  };
}

function computeCell() {
  const maxW = Math.max(window.innerWidth - CHROME_W, 100);
  const maxH = Math.max(window.innerHeight - CHROME_H, 100);
  return Math.max(8, Math.min(Math.floor(maxW / COLS), Math.floor(maxH / ROWS)));
}

function makeFormation() {
  const inv = [];
  for (let r = 0; r < ALIEN_ROWS; r++) {
    for (let c = 0; c < ALIEN_COLS; c++) {
      inv.push({ c, r });
    }
  }
  return inv;
}

function formationRightX(invaders, formationX) {
  if (!invaders.length) return formationX;
  const maxC = Math.max(...invaders.map((a) => a.c));
  return formationX + maxC * STEP + ALIEN_CELL;
}

function formationLeftX(invaders, formationX) {
  if (!invaders.length) return formationX;
  const minC = Math.min(...invaders.map((a) => a.c));
  return formationX + minC * STEP;
}

function formationBottomY(invaders, formationY) {
  if (!invaders.length) return formationY;
  const maxR = Math.max(...invaders.map((a) => a.r));
  return formationY + maxR * STEP + ALIEN_CELL;
}

function alienRect(a, formationX, formationY) {
  return {
    x: formationX + a.c * STEP,
    y: formationY + a.r * STEP,
    w: ALIEN_CELL,
    h: ALIEN_CELL,
  };
}

function rectsOverlap(ax, ay, aw, ah, bx, by, bw, bh) {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

function SpaceInvadersGame() {
  useMinisiteSessionAnalytics();
  const canvasRef = useRef(null);
  const cellRef = useRef(computeCell());
  const [canvasW, setCanvasW] = useState(() => cellRef.current * COLS);
  const [canvasH, setCanvasH] = useState(() => cellRef.current * ROWS);

  const stateRef = useRef({
    invaders: [],
    formationX: 2,
    formationY: 2,
    formationDir: 1,
    playerX: Math.floor((COLS - SHIP_W) / 2),
    playerBullet: null,
    enemyBullets: [],
    shieldCharges: SHIELD_CHARGES_MAX,
    absorbNextHit: false,
    lives: LIVES_START,
    shotsSinceBonus: 0,
    nextBonusShotThreshold: randomBonusShotThreshold(),
    bonusShip: null,
    bonusExplosion: null,
    score: 0,
    wave: 1,
    phase: 'idle',
    frameId: null,
    lastTime: 0,
    alienAcc: 0,
    alienMoveMs: 520,
    bulletAcc: 0,
    bulletTickMs: 55,
    playerMoveAcc: 0,
    playerMoveMs: 72,
    enemyShootAcc: 0,
    highScore: 0,
    keys: { ArrowLeft: false, ArrowRight: false },
  });

  const [displayScore, setDisplayScore] = useState(0);
  const [displayWave, setDisplayWave] = useState(1);
  const [displayShields, setDisplayShields] = useState(SHIELD_CHARGES_MAX);
  const [displayLives, setDisplayLives] = useState(LIVES_START);
  const [phase, setPhase] = useState('idle');

  const [screen, setScreen] = useState('leaders');
  const [deadStep, setDeadStep] = useState('idle');
  const [joinSel, setJoinSel] = useState('no');
  const [nameInput, setNameInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const prevScreenRef = useRef('leaders');

  const goToLeaders = useCallback(() => {
    const s = stateRef.current;
    cancelAnimationFrame(s.frameId);
    s.phase = 'idle';
    s.frameId = null;
    setPhase('idle');
    setDeadStep('idle');
    setNameInput('');
    setJoinSel('no');
    setScreen('leaders');
  }, []);

  useEffect(() => {
    const onResize = () => {
      const c = computeCell();
      cellRef.current = c;
      setCanvasW(c * COLS);
      setCanvasH(c * ROWS);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const cell = cellRef.current;
    const w = cell * COLS;
    const h = cell * ROWS;
    const s = stateRef.current;

    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 0.5;
    for (let x = 0; x <= COLS; x++) {
      ctx.beginPath();
      ctx.moveTo(x * cell, 0);
      ctx.lineTo(x * cell, h);
      ctx.stroke();
    }
    for (let y = 0; y <= ROWS; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * cell);
      ctx.lineTo(w, y * cell);
      ctx.stroke();
    }

    const alienVariant = Math.max(0, (s.wave - 1) % ALIEN_SHIP_VARIANTS);
    s.invaders.forEach((a) => {
      const r = alienRect(a, s.formationX, s.formationY);
      const px = r.x * cell;
      const py = r.y * cell;
      drawAlienShip(ctx, px + 1, py + 1, r.w * cell - 2, r.h * cell - 2, alienVariant);
    });

    const px0 = s.playerX * cell;
    const py0 = PLAYER_ROW * cell;
    const pW = SHIP_W * cell;
    drawMobileCannon(ctx, px0, py0, pW, cell, cell);

    if (s.playerBullet) {
      const b = s.playerBullet;
      ctx.fillStyle = COLORS.bulletPlayer;
      ctx.fillRect(b.x * cell + cell * 0.4, b.y * cell, cell * 0.2, cell * 0.6);
    }

    s.enemyBullets.forEach((b) => {
      ctx.fillStyle = COLORS.bulletEnemy;
      ctx.fillRect(b.x * cell + cell * 0.35, b.y * cell, cell * 0.3, cell * 0.55);
    });

    if (s.bonusShip) {
      const bs = s.bonusShip;
      const bx = bs.x * cell;
      const by = bs.y * cell;
      const bw = bs.w * cell;
      const bh = bs.h * cell;
      drawLargeBonusShip(ctx, bx + 1, by + 1, bw - 2, bh - 2);
    }

    if (s.bonusExplosion) {
      const prog = 1 - s.bonusExplosion.framesLeft / EXPLOSION_FRAMES;
      drawBonusExplosion(ctx, cell, s.bonusExplosion.gx, s.bonusExplosion.gy, prog);
    }
  }, []);

  const spawnWave = useCallback((s, waveNum) => {
    s.invaders = makeFormation();
    s.formationX = 2;
    s.formationY = 2;
    s.formationDir = 1;
    s.playerBullet = null;
    s.enemyBullets = [];
    s.wave = waveNum;
    s.alienMoveMs = Math.max(220, 520 - (waveNum - 1) * 45);
    s.enemyShootAcc = 0;
  }, []);

  const startGame = useCallback(() => {
    const s = stateRef.current;
    cancelAnimationFrame(s.frameId);
    s.frameId = null;
    s.playerX = Math.floor((COLS - SHIP_W) / 2);
    s.shieldCharges = SHIELD_CHARGES_MAX;
    s.absorbNextHit = false;
    s.lives = LIVES_START;
    s.shotsSinceBonus = 0;
    s.nextBonusShotThreshold = randomBonusShotThreshold();
    s.bonusShip = null;
    s.bonusExplosion = null;
    s.score = 0;
    s.highScore = 0;
    s.phase = 'running';
    s.lastTime = 0;
    s.alienAcc = 0;
    s.bulletAcc = 0;
    s.playerMoveAcc = 0;
    s.keys = { ArrowLeft: false, ArrowRight: false };
    spawnWave(s, 1);
    setDisplayScore(0);
    setDisplayWave(1);
    setDisplayShields(SHIELD_CHARGES_MAX);
    setDisplayLives(LIVES_START);
    setPhase('running');
    draw();
  }, [draw, spawnWave]);

  const gameOver = useCallback(
    (s) => {
      s.phase = 'dead';
      if (s.score > s.highScore) s.highScore = s.score;
      const capped = Math.min(Math.floor(s.score), SCORE_CAP);
      s.score = capped;
      setDisplayScore(capped);
      setPhase('dead');
      draw();
    },
    [draw]
  );

  const tryEnemyShoot = useCallback((s) => {
    if (!s.invaders.length) return;
    const shooter = s.invaders[Math.floor(Math.random() * s.invaders.length)];
    const r = alienRect(shooter, s.formationX, s.formationY);
    const bx = r.x + r.w / 2 - 0.15;
    const by = r.y + r.h;
    s.enemyBullets.push({ x: bx, y: by });
  }, []);

  const gameLoop = useCallback(
    (timestamp) => {
      const s = stateRef.current;
      if (s.phase !== 'running') return;

      if (!s.lastTime) {
        s.lastTime = timestamp;
        draw();
        s.frameId = requestAnimationFrame(gameLoop);
        return;
      }
      const dt = Math.min(80, timestamp - s.lastTime);
      s.lastTime = timestamp;

      if (s.bonusExplosion && s.bonusExplosion.framesLeft > 0) {
        s.bonusExplosion.framesLeft -= 1;
        if (s.bonusExplosion.framesLeft <= 0) s.bonusExplosion = null;
      }

      s.playerMoveAcc += dt;
      while (s.playerMoveAcc >= s.playerMoveMs) {
        s.playerMoveAcc -= s.playerMoveMs;
        if (s.keys.ArrowLeft) s.playerX = Math.max(0, s.playerX - 1);
        if (s.keys.ArrowRight) s.playerX = Math.min(COLS - SHIP_W, s.playerX + 1);
      }

      s.alienAcc += dt;
      if (s.invaders.length && s.alienAcc >= s.alienMoveMs) {
        s.alienAcc = 0;
        let nx = s.formationX + s.formationDir;
        const rEdge = formationRightX(s.invaders, nx);
        const lEdge = formationLeftX(s.invaders, nx);
        if (rEdge >= COLS || lEdge < 0) {
          s.formationDir *= -1;
          s.formationY += 1;
        } else {
          s.formationX = nx;
        }
        if (formationBottomY(s.invaders, s.formationY) >= PLAYER_ROW) {
          gameOver(s);
          return;
        }
      }

      s.enemyShootAcc += dt;
      if (s.invaders.length && s.enemyShootAcc >= 1400) {
        s.enemyShootAcc = 0;
        tryEnemyShoot(s);
      }

      s.bulletAcc += dt;
      if (s.bulletAcc >= s.bulletTickMs) {
        s.bulletAcc = 0;
        if (s.playerBullet) {
          s.playerBullet.y -= 0.55;
          if (s.playerBullet.y < 0) s.playerBullet = null;
        }
        s.enemyBullets = s.enemyBullets
          .map((b) => ({ ...b, y: b.y + 0.42 }))
          .filter((b) => b.y < ROWS + 1);

        if (s.bonusShip) {
          s.bonusShip.y += s.bonusShip.vy;
          if (s.bonusShip.y + s.bonusShip.h < -0.5) s.bonusShip = null;
        }
      }

      if (s.playerBullet) {
        const pb = s.playerBullet;
        let hitBonus = false;
        if (s.bonusShip) {
          const bs = s.bonusShip;
          if (rectsOverlap(pb.x, pb.y, 0.35, 0.7, bs.x, bs.y, bs.w, bs.h)) {
            hitBonus = true;
            s.bonusExplosion = {
              gx: bs.x + bs.w / 2,
              gy: bs.y + bs.h / 2,
              framesLeft: EXPLOSION_FRAMES,
            };
            s.bonusShip = null;
            s.playerBullet = null;
            s.lives = Math.min(LIVES_MAX, s.lives + 1);
            setDisplayLives(s.lives);
          }
        }
        if (!hitBonus) {
          const hitIdx = s.invaders.findIndex((a) => {
            const r = alienRect(a, s.formationX, s.formationY);
            return rectsOverlap(pb.x, pb.y, 0.35, 0.7, r.x, r.y, r.w, r.h);
          });
          if (hitIdx >= 0) {
            const a = s.invaders[hitIdx];
            const rowBonus = (ALIEN_ROWS - a.r) * 15;
            s.score = Math.min(SCORE_CAP, s.score + 40 + rowBonus);
            setDisplayScore(s.score);
            s.invaders.splice(hitIdx, 1);
            s.playerBullet = null;
            if (s.invaders.length === 0) {
              s.wave += 1;
              s.score = Math.min(SCORE_CAP, s.score + 100 * s.wave);
              setDisplayScore(s.score);
              setDisplayWave(s.wave);
              spawnWave(s, s.wave);
            }
          }
        }
      }

      const shipY0 = PLAYER_ROW;
      const shipY1 = PLAYER_ROW + 1;
      for (let i = s.enemyBullets.length - 1; i >= 0; i--) {
        const b = s.enemyBullets[i];
        if (
          b.y >= shipY0 - 0.3 &&
          b.y <= shipY1 + 0.5 &&
          b.x >= s.playerX - 0.2 &&
          b.x <= s.playerX + SHIP_W + 0.2
        ) {
          s.enemyBullets.splice(i, 1);
          if (s.absorbNextHit) {
            s.absorbNextHit = false;
          } else if (s.lives <= 1) {
            gameOver(s);
            return;
          } else {
            s.lives -= 1;
            setDisplayLives(s.lives);
            s.enemyBullets = [];
          }
        }
      }

      draw();
      s.frameId = requestAnimationFrame(gameLoop);
    },
    [draw, gameOver, spawnWave, tryEnemyShoot]
  );

  useEffect(() => {
    draw();
  }, [draw, canvasW, canvasH]);

  useEffect(() => {
    const was = prevScreenRef.current;
    prevScreenRef.current = screen;
    if (screen !== 'game') return undefined;
    if (was !== 'leaders') return undefined;
    startGame();
    const s = stateRef.current;
    s.frameId = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(s.frameId);
  }, [screen, startGame, gameLoop]);

  useEffect(() => {
    if (phase !== 'dead') return undefined;
    let cancelled = false;
    setDeadStep('loading');
    (async () => {
      try {
        const d = await fetchMinisiteLeaderboard('invaders');
        if (cancelled) return;
        const entries = d.entries || [];
        const sc = stateRef.current.score;
        const show = qualifiesForLeaderboard('invaders', entries, sc);
        setDeadStep(show ? 'joinAsk' : 'restartOnly');
      } catch {
        if (!cancelled) setDeadStep('restartOnly');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase]);

  useEffect(() => {
    if (phase === 'running') setDeadStep('idle');
  }, [phase]);

  useEffect(() => {
    const onKeyUp = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        stateRef.current.keys[e.key] = false;
      }
    };
    window.addEventListener('keyup', onKeyUp);
    return () => window.removeEventListener('keyup', onKeyUp);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const s = stateRef.current;

      if (deadStep === 'name') return;

      if (phase === 'dead' && deadStep === 'loading') {
        e.preventDefault();
        return;
      }

      if (phase === 'dead' && deadStep === 'joinAsk') {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          setJoinSel((v) => (v === 'no' ? 'yes' : 'no'));
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          if (joinSel === 'yes') {
            setNameInput('');
            setDeadStep('name');
            return;
          }
          goToLeaders();
        }
        return;
      }

      if (phase === 'dead' && deadStep === 'thanks') {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          goToLeaders();
        }
        return;
      }

      if (e.key === 'Enter' || e.keyCode === 13) {
        if (phase === 'dead' && deadStep === 'restartOnly') {
          cancelAnimationFrame(s.frameId);
          startGame();
          s.frameId = requestAnimationFrame(gameLoop);
        }
        e.preventDefault();
        return;
      }

      if (s.phase === 'running') {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          s.keys[e.key] = true;
          e.preventDefault();
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (!s.playerBullet) {
            s.playerBullet = {
              x: s.playerX + SHIP_W / 2 - 0.1,
              y: PLAYER_ROW - 0.8,
            };
            if (!s.bonusShip) {
              s.shotsSinceBonus += 1;
              if (s.shotsSinceBonus >= s.nextBonusShotThreshold) {
                s.bonusShip = spawnBonusShip();
                s.shotsSinceBonus = 0;
                s.nextBonusShotThreshold = randomBonusShotThreshold();
              }
            }
          }
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (s.shieldCharges > 0) {
            s.shieldCharges -= 1;
            s.absorbNextHit = true;
            setDisplayShields(s.shieldCharges);
          }
          return;
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [startGame, gameLoop, phase, deadStep, joinSel, goToLeaders]);

  const handleNameDone = useCallback(async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await submitMinisiteLeaderboard('invaders', trimmed, Math.floor(stateRef.current.score));
      setDeadStep('thanks');
    } catch {
      setDeadStep('thanks');
    } finally {
      setSubmitting(false);
    }
  }, [nameInput, submitting]);

  const handleNameCancel = useCallback(() => {
    setDeadStep('joinAsk');
    setNameInput('');
  }, []);

  const onLeadersContinue = useCallback(() => {
    setScreen('game');
  }, []);

  if (screen === 'leaders') {
    return (
      <MonthlyLeadersScreen
        game="invaders"
        scoreLabel="ניקוד"
        onContinue={onLeadersContinue}
      />
    );
  }

  return (
    <div className="invaders-page" dir="rtl">
      <div className="invaders-header">
        <div className="invaders-title">חייזרים</div>
        <div className="invaders-hud">
          <div className="invaders-hud-item">
            <span className="invaders-hud-label">ניקוד</span>
            <span className="invaders-hud-value">{displayScore}</span>
          </div>
          <div className="invaders-hud-item">
            <span className="invaders-hud-label">גל</span>
            <span className="invaders-hud-value">{displayWave}</span>
          </div>
          <div className="invaders-hud-item">
            <span className="invaders-hud-label">מגנים</span>
            <span className="invaders-hud-value">{displayShields}</span>
          </div>
          <div className="invaders-hud-item">
            <span className="invaders-hud-label">חיים</span>
            <span className="invaders-hud-value">{displayLives}</span>
          </div>
        </div>
      </div>

      <div className="invaders-canvas-wrap">
        <canvas ref={canvasRef} width={canvasW} height={canvasH} />

        {phase === 'dead' && (
          <div className="invaders-overlay">
            <div className="invaders-overlay-title">סוף המשחק</div>
            <div className="invaders-overlay-score">ניקוד: {displayScore}</div>
            {deadStep === 'loading' && <div className="minisite-overlay-busy">בודקים זכאות לטבלה…</div>}
            {deadStep === 'restartOnly' && (
              <div className="invaders-overlay-hint">לחץ Enter להתחלה מחדש</div>
            )}
            {deadStep === 'joinAsk' && (
              <div className="minisite-join">
                <p className="minisite-join-q">להצטרף למצטייני החודש?</p>
                <div className="minisite-join-options">
                  <span className={`minisite-join-opt ${joinSel === 'no' ? 'minisite-join-opt--on' : ''}`}>לא</span>
                  <span className={`minisite-join-opt ${joinSel === 'yes' ? 'minisite-join-opt--on' : ''}`}>כן</span>
                </div>
                <div className="invaders-overlay-hint">← → לבחירה · Enter לאישור</div>
              </div>
            )}
            {deadStep === 'name' && (
              <RemoteNameKeyboard
                value={nameInput}
                onChange={setNameInput}
                onDone={handleNameDone}
                onCancel={handleNameCancel}
              />
            )}
            {deadStep === 'thanks' && (
              <div className="invaders-overlay-hint">נרשמת בהצלחה! לחץ Enter לחזרה למצטייני החודש</div>
            )}
            {submitting && <div className="minisite-overlay-busy">שומר…</div>}
          </div>
        )}
      </div>

      <div className="invaders-footer">
        ← → לזוז · ↑ לירות · ↓ מגן (מלאי מוגבל) · 3 חיים · חללית בונוס (+חיים) · ללא קול
      </div>
    </div>
  );
}

export default SpaceInvadersGame;
